// ============================================
// Path: /frontend/src/pages/admin/JointVenturesAdminPage.jsx
// Purpose: Admin page to view, manage, and match joint venture registrations
// ============================================

import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { jointVenturesAPI } from '../../api/jointVentures.api';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../hooks/useToast';
import { Eye, RefreshCw, Users, Building, Wallet, CheckCircle2, Clock, XCircle, Sparkles } from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';

// ========================================
// LOADING SKELETON
// ========================================
const LoadingSkeleton = () => (
  <CardContent>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>User</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Location</TableHead>
          <TableHead>Date</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 5 }).map((_, i) => (
          <TableRow key={i}>
            <TableCell><Skeleton className="h-5 w-40" /></TableCell>
            <TableCell><Skeleton className="h-6 w-24" /></TableCell>
            <TableCell><Skeleton className="h-6 w-24" /></TableCell>
            <TableCell><Skeleton className="h-5 w-32" /></TableCell>
            <TableCell><Skeleton className="h-5 w-32" /></TableCell>
            <TableCell><Skeleton className="h-8 w-20" /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </CardContent>
);

// ========================================
// STATUS CONFIG
// ========================================
const STATUS_CONFIG = {
  pending: { label: 'Pending', variant: 'secondary', icon: Clock },
  active: { label: 'Active', variant: 'default', icon: CheckCircle2 },
  matched: { label: 'Matched', variant: 'default', icon: CheckCircle2 },
  connected: { label: 'Connected', variant: 'default', icon: CheckCircle2 },
  rejected: { label: 'Rejected', variant: 'destructive', icon: XCircle }
};

// ========================================
// VIEW DETAILS DIALOG
// ========================================

// ========================================
// VIEW DETAILS DIALOG - FIXED
// Replace the ViewDetailsDialog component in JointVenturesAdminPage.jsx
// ========================================
const ViewDetailsDialog = ({ venture }) => {
  const isLandowner = venture.role === 'LAND_OWNER';

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Eye className="h-4 w-4 mr-2" />
          View
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Submission Details</DialogTitle>
          <DialogDescription>
            {venture.userEmail} • {isLandowner ? 'Land Owner' : 'Investor'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Personal Info */}
          <div>
            <h4 className="font-semibold mb-2">Contact Information</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground">Name</p>
                <p>{venture.name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Phone</p>
                <p>{venture.phone || 'N/A'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Email</p>
                <p>{venture.userEmail}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Submitted</p>
                <p>{format(new Date(venture.createdAt), 'PPP')}</p>
              </div>
            </div>
          </div>

          {/* Location */}
          {venture.county && (
            <div>
              <h4 className="font-semibold mb-2">Location</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">County</p>
                  <p>{venture.county}</p>
                </div>
                {venture.subCounty && (
                  <div>
                    <p className="text-muted-foreground">Sub-County</p>
                    <p>{venture.subCounty}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Landowner Details */}
          {isLandowner && (
            <div>
              <h4 className="font-semibold mb-2">Land Details</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {venture.landSize && (
                  <div>
                    <p className="text-muted-foreground">Land Size</p>
                    <p>{venture.landSize} Acres</p>
                  </div>
                )}
                {venture.titleDeedStatus && (
                  <div>
                    <p className="text-muted-foreground">Title Deed</p>
                    <p className="capitalize">{venture.titleDeedStatus}</p>
                  </div>
                )}
                {venture.developmentType && (
                  <div>
                    <p className="text-muted-foreground">Development Type</p>
                    <p>{venture.developmentType}</p>
                  </div>
                )}
                {venture.landValue && (
                  <div>
                    <p className="text-muted-foreground">Land Value</p>
                    <p>KES {venture.landValue.toLocaleString()}</p>
                  </div>
                )}
                {venture.capitalNeeded && (
                  <div>
                    <p className="text-muted-foreground">Capital Needed</p>
                    <p>KES {venture.capitalNeeded.toLocaleString()}</p>
                  </div>
                )}
                {venture.timeline && (
                  <div>
                    <p className="text-muted-foreground">Timeline</p>
                    <p>{venture.timeline}</p>
                  </div>
                )}
              </div>
              {venture.details && (
                <div className="mt-3">
                  <p className="text-muted-foreground text-sm">Additional Details</p>
                  <p className="text-sm whitespace-pre-wrap">{venture.details}</p>
                </div>
              )}
            </div>
          )}

          {/* Investor Details */}
          {!isLandowner && (
            <div>
              <h4 className="font-semibold mb-2">Investment Details</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {venture.investmentAmount && (
                  <div>
                    <p className="text-muted-foreground">Investment Amount</p>
                    <p>KES {venture.investmentAmount.toLocaleString()}</p>
                  </div>
                )}
                {venture.experienceLevel && (
                  <div>
                    <p className="text-muted-foreground">Experience</p>
                    <p className="capitalize">{venture.experienceLevel.replace('_', ' ')}</p>
                  </div>
                )}
                {venture.investmentMin && (
                  <div>
                    <p className="text-muted-foreground">Min Investment</p>
                    <p>KES {venture.investmentMin.toLocaleString()}</p>
                  </div>
                )}
                {venture.investmentMax && (
                  <div>
                    <p className="text-muted-foreground">Max Investment</p>
                    <p>KES {venture.investmentMax.toLocaleString()}</p>
                  </div>
                )}
                {venture.projectTypeInterest && (
                  <div>
                    <p className="text-muted-foreground">Project Interest</p>
                    <p>{venture.projectTypeInterest}</p>
                  </div>
                )}
                {venture.landSizeMin && venture.landSizeMax && (
                  <div>
                    <p className="text-muted-foreground">Land Size Range</p>
                    <p>{venture.landSizeMin} - {venture.landSizeMax} Acres</p>
                  </div>
                )}
                {venture.timeline && (
                  <div>
                    <p className="text-muted-foreground">Timeline</p>
                    <p>{venture.timeline}</p>
                  </div>
                )}
              </div>
              {venture.details && (
                <div className="mt-3">
                  <p className="text-muted-foreground text-sm">Additional Notes</p>
                  <p className="text-sm whitespace-pre-wrap">{venture.details}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ========================================
// UPDATE STATUS DIALOG
// ========================================
const UpdateStatusDialog = ({ venture, onUpdate }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(venture.status || 'pending');
  const [adminNotes, setAdminNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUpdate = async () => {
    setIsSubmitting(true);
    try {
      await jointVenturesAPI.updateStatus(venture.id, { status, adminNotes });
      toast({ title: 'Status updated successfully' });
      onUpdate();
      setOpen(false);
    } catch (error) {
      toast({ 
        variant: 'destructive', 
        title: 'Update failed', 
        description: error.response?.data?.message 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Update
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update Submission Status</DialogTitle>
          <DialogDescription>
            Change status and add admin notes
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending Review</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="matched">Matched</SelectItem>
                <SelectItem value="connected">Connected</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Admin Notes (Optional)</Label>
            <Textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Add notes about this submission..."
              rows={4}
            />
          </div>

          <Button 
            onClick={handleUpdate} 
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? 'Updating...' : 'Update Status'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ========================================
// MAIN COMPONENT
// ========================================
export default function JointVenturesAdminPage() {
  const { toast } = useToast();
  const [ventures, setVentures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [matchResults, setMatchResults] = useState(null);
  const [isMatching, setIsMatching] = useState(false);

  // ========================================
  // FETCH DATA
  // ========================================
  const fetchVentures = async () => {
    setLoading(true);
    try {
      const data = await jointVenturesAPI.getAllJointVentures();
      setVentures(data.jointVentures || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load joint ventures');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVentures();
  }, []);

  // ========================================
  // RUN MATCHING ALGORITHM
  // ========================================
  const runMatching = async () => {
    setIsMatching(true);
    try {
      const data = await jointVenturesAPI.runMatching();
      setMatchResults(data.matches);
      toast({ 
        title: 'Matching Complete', 
        description: `Found ${data.matches?.length || 0} potential matches` 
      });
    } catch (error) {
      toast({ 
        variant: 'destructive', 
        title: 'Matching failed', 
        description: error.response?.data?.message 
      });
    } finally {
      setIsMatching(false);
    }
  };

  // ========================================
  // FILTER VENTURES
  // ========================================
  const filteredVentures = ventures.filter(v => {
    if (activeTab === 'all') return true;
    if (activeTab === 'landowners') return v.role === 'LAND_OWNER';
    if (activeTab === 'investors') return v.role === 'INVESTOR';
    return v.status === activeTab;
  });

  // ========================================
  // STATS
  // ========================================
  const stats = {
    total: ventures.length,
    landowners: ventures.filter(v => v.role === 'LAND_OWNER').length,
    investors: ventures.filter(v => v.role === 'INVESTOR').length,
    pending: ventures.filter(v => v.status === 'pending').length,
    active: ventures.filter(v => v.status === 'active').length,
    matched: ventures.filter(v => v.status === 'matched').length
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Submissions</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              {stats.landowners} landowners, {stats.investors} investors
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active}</div>
            <p className="text-xs text-muted-foreground">
              Ready for matching
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Matched</CardTitle>
            <Sparkles className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.matched}</div>
            <p className="text-xs text-muted-foreground">
              Potential partnerships
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Matching Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Matching Algorithm</CardTitle>
              <CardDescription>Find potential matches between landowners and investors</CardDescription>
            </div>
            <Button onClick={runMatching} disabled={isMatching}>
              {isMatching ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Matching...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Run Matching
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        {matchResults && matchResults.length > 0 && (
          <CardContent>
            <Alert>
              <Sparkles className="h-4 w-4" />
              <AlertDescription>
                Found {matchResults.length} potential matches with 90%+ compatibility score!
              </AlertDescription>
            </Alert>
          </CardContent>
        )}
      </Card>

      {/* Main Table */}
      <Card>
        <CardHeader>
          <CardTitle>Joint Venture Submissions</CardTitle>
          <CardDescription>Review and manage all submissions</CardDescription>
        </CardHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="px-6">
            <TabsList>
              <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
              <TabsTrigger value="landowners">Landowners ({stats.landowners})</TabsTrigger>
              <TabsTrigger value="investors">Investors ({stats.investors})</TabsTrigger>
              <TabsTrigger value="pending">Pending ({stats.pending})</TabsTrigger>
              <TabsTrigger value="active">Active ({stats.active})</TabsTrigger>
              <TabsTrigger value="matched">Matched ({stats.matched})</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value={activeTab}>
            {loading ? (
              <LoadingSkeleton />
            ) : error ? (
              <CardContent>
                <p className="text-destructive">Error: {error}</p>
              </CardContent>
            ) : (
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                {filteredVentures.map((venture) => {
                  const statusInfo = STATUS_CONFIG[venture.status] || STATUS_CONFIG.pending;
                  
                  return (
                    <TableRow key={venture.id}>
                      <TableCell className="font-medium">{venture.userEmail}</TableCell>
                      <TableCell>
                        <Badge variant={venture.role === 'INVESTOR' ? 'default' : 'secondary'}>
                          {venture.role === 'LAND_OWNER' ? (
                            <><Building className="h-3 w-3 mr-1" /> Landowner</>
                          ) : (
                            <><Wallet className="h-3 w-3 mr-1" /> Investor</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                      </TableCell>
                      <TableCell>{venture.county || 'N/A'}</TableCell>
                      <TableCell>{format(new Date(venture.createdAt), 'PP')}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <ViewDetailsDialog venture={venture} />
                          <UpdateStatusDialog venture={venture} onUpdate={fetchVentures} />
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}

                  </TableBody>
                </Table>
                {filteredVentures.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No submissions found.</p>
                )}
              </CardContent>
            )}
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}